import type { ServiceProviderProps } from "@/providers/SRP";
import { applyURLSearchParams } from "@/utils";
import { omit } from "ramda";
import { createStore } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { MSG_INIT_FUNDS, SEARCH_PROP, URL_SEARCH_PARAM } from "../constants";
import applyFilters from "../lib/applyFilters";
import { formatter } from "../lib/format";
import transformer from "../lib/transform";
import type { Fund, StoreActions, StoreState } from "../types";

// const worker = new Worker(
// 	new URL("../lib/applyFilters.worker.ts", import.meta.url),
// 	{ type: "module" },
// );

const defaultState: Omit<StoreState, "srpProps"> = {
	activeFilters: {},
	activeSearch: "",
	apiResponse: null,
	appliedFilters: {},
	asAtDate: null,
	filteredEntities: [],
	filteredFunds: [],
	funds: [],
	indexedData: {},
	isLoading: true,
	loadingStatus: MSG_INIT_FUNDS,
	locale: "en",
	profiles: [],
};

const storeCreator = (
	tenant: string,
	srpProps: ServiceProviderProps,
	initialState?: Partial<Omit<StoreState, "srpProps">>,
) =>
	createStore<StoreState & StoreActions>()(
		devtools(
			immer(
				(set, get) =>
					({
						srpProps,
						...{ ...defaultState, ...initialState },
						refine(applyURLParams = true) {
							const config = get().config;
							if (!config) {
								return;
							}
							get().__doUpdateFilteredFunds(
								applyFilters({
									data: get().indexedData,
									filters: {
										...get().activeFilters,
										[SEARCH_PROP]: [get().activeSearch],
									},
									config,
								}),
							);
							applyURLParams &&
								applyURLSearchParams({
									...get().activeFilters,
									[URL_SEARCH_PARAM]: get().activeSearch,
								});
						},
						doFilter(entityKey, optionKeys) {
							set(
								(state) => {
									state.activeFilters[entityKey] = optionKeys;
								},
								false,
								{ type: `doFilter: ${entityKey}=${optionKeys}` },
							);
							get().refine();
						},
						doFilterReset(args) {
							set(
								(state) => {
									const { exclude, include } = args ?? {};
									let keys = include ?? Object.keys(state.activeFilters);
									keys = exclude
										? keys.filter((key) => !exclude.includes(key))
										: keys;
									// state.activeFilters = omit(keys, state.activeFilters);
									for (const key of keys) {
										state.activeFilters[key] = [];
									}
								},
								false,
								{
									type: `doFilterReset: ${args ? JSON.stringify(args) : "all"}`,
								},
							);
							get().refine();
						},
						doGetFilterOptionResultsCount(filterKey, optionKey) {
							const { activeFilters, activeSearch, config, indexedData } =
								get();
							if (!config) {
								return 0;
							}
							// const searchResults =
							// 	activeSearch.trim().length > 0
							// 		? applyFilters({
							// 				data: indexedData,
							// 				filters: { [SEARCH_PROP]: [activeSearch] },
							// 				config,
							// 			})
							// 		: funds;
							const filters = {
								...activeFilters,
								[filterKey]: [optionKey],
								[SEARCH_PROP]: [activeSearch],
							};
							// const results = applyFilters({
							// 	data: indexedData,
							// 	filters,
							// 	config,
							// });
							return applyFilters({
								data: indexedData,
								filters,
								config,
							}).length;
							// return results.filter((r) =>
							// 	searchResults.some((sr) => sr.localId === r.localId),
							// ).length;
						},
						doSearch(term) {
							set((state) => {
								state.activeSearch = term;
							});
							get().refine();
						},
						doSearchReset() {
							set((state) => {
								state.activeSearch = "";
							});
							get().refine();
						},
						init(config, data) {
							set((state) => {
								state.isLoading = false;
								state.config = config;
								state.apiResponse = data;
								state.asAtDate = data.metadata.as_at_date;
								state.indexedData = transformer(formatter, data);

								const indexedValues = Object.values(state.indexedData);
								for (const value of indexedValues) {
									if (value.type === "fund") {
										state.funds.push(value.data);
									}
									if (value.type === "profile") {
										state.profiles.push(value.data);
									}
								}
								state.filteredFunds = state.funds.slice();
							});
							get().refine(false);
						},
						doSetLoadingStatus(status) {
							set({ loadingStatus: status }, false, {
								type: `doSetLoadingStatus: ${status}`,
							});
						},
						__doUpdateFilteredFunds(filteredFunds) {
							set({ filteredFunds }, false, {
								type: `__doUpdateFilteredFunds: ${filteredFunds.length}`,
							});
						},
						__setState(state) {
							set((s) => ({ ...s, ...state }), false, {
								type: "__setState",
							});
							get().refine(false);
						},
					}) as StoreState & StoreActions,
			),
			{
				name: "FE Kernel",
				enabled: process.env.NODE_ENV !== "production",
				store: `${tenant}-${(Math.random() * 100).toFixed(0)}`,
			},
		),
	);

export type FundExplorerStore = ReturnType<typeof storeCreator>;
export default storeCreator;
